/**
 * Design Collaboration Bootstrap
 *
 * Client-side bootstrap helpers for Space Design collaboration sessions.
 *
 * Primary responsibilities:
 * - Fetch typed collaboration session metadata
 * - Normalize bootstrap responses before adapter creation
 *
 * Dependencies:
 * - Design collaboration protocol helpers
 */

import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import {
  parseDesignCollaborationBootstrapResponse
} from '@/lib/spaces/design-collaboration/adapter'
import type {
  DesignCollaborationBootstrapResponse,
  DesignCollaborationSession
} from '@/lib/spaces/design-collaboration/types'

interface FetchDesignCollaborationSessionOptions {
  designId: string
  signal?: AbortSignal
  spaceId: string
}

// --------------

function buildBootstrapRoute(options: FetchDesignCollaborationSessionOptions) {
  const encodedSpaceId = encodeURIComponent(options.spaceId)
  const encodedDesignId = encodeURIComponent(options.designId)

  return `/api/spaces/${encodedSpaceId}/designs/${encodedDesignId}/collaboration`
}

// --------------

async function readBootstrapError(response: Response) {
  try {
    const body = (await response.json()) as {
      error?: string
      message?: string
    }

    if (typeof body.error === 'string' && body.error.trim()) {
      return body.error
    }

    if (typeof body.message === 'string' && body.message.trim()) {
      return body.message
    }
  } catch {}

  const fallbackText = await response.text().catch(() => '')
  return fallbackText || `Failed to bootstrap collaboration (${response.status})`
}

// --------------

export async function fetchDesignCollaborationSession(
  options: FetchDesignCollaborationSessionOptions
): Promise<DesignCollaborationSession> {
  const response = await authenticatedFetch(buildBootstrapRoute(options), {
    cache: 'no-store',
    headers: {
      Accept: 'application/json'
    },
    method: 'GET',
    signal: options.signal
  })

  if (!response.ok) {
    throw new Error(await readBootstrapError(response))
  }

  const json = (await response.json()) as DesignCollaborationBootstrapResponse
  return parseDesignCollaborationBootstrapResponse(json).collaboration
}
